import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Typography, Box, Alert,
  Card, CardContent, Chip, IconButton, Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
// import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import ErrorIcon from '@mui/icons-material/Error';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { resolveApiUrl } from '../../../utils/fetchJson';

const normalizeHeader = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const pickValue = (row, keys = []) => {
  const normalizedEntries = Object.entries(row || {}).map(([key, value]) => [normalizeHeader(key), value]);
  for (const key of keys) {
    const normalizedKey = normalizeHeader(key);
    const found = normalizedEntries.find(([entryKey]) => entryKey === normalizedKey);
    if (found && found[1] !== undefined && found[1] !== null && String(found[1]).trim() !== '') {
      return found[1];
    }
  }
  return '';
};

const normalizeSex = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (['m', 'male', 'boy'].includes(normalized)) return 'Male';
  if (['f', 'female', 'girl'].includes(normalized)) return 'Female';
  return value;
};

const normalizeDob = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed && parsed.y && parsed.m && parsed.d) {
      const day = String(parsed.d).padStart(2, '0');
      const month = String(parsed.m).padStart(2, '0');
      return `${day}-${month}-${parsed.y}`;
    }
  }
  const raw = String(value).trim();
  if (!raw) return '';
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[1].padStart(2, '0')}-${slashMatch[2].padStart(2, '0')}-${slashMatch[3]}`;
  return raw;
};

const toStudentRow = (row, index) => ({
  id: index + 1,
  firstName: String(pickValue(row, ['firstName', 'firstname', 'first_name', 'first name', 'student first name'])).trim(),
  lastName: String(pickValue(row, ['lastName', 'lastname', 'last_name', 'surname', 'last name', 'student last name'])).trim(),
  dob: normalizeDob(pickValue(row, ['dob', 'dateOfBirth', 'date_of_birth', 'date of birth', 'date of birth ddmmyyyy'])),
  sex: normalizeSex(pickValue(row, ['sex', 'gender', 'student gender'])),
  placeOfBirth: String(pickValue(row, ['placeOfBirth', 'place_of_birth', 'place of birth'])).trim(),
  address: String(pickValue(row, ['address', 'homeAddress', 'home_address', 'home address'])).trim(),
  className: String(pickValue(row, ['className', 'class', 'classname', 'class name'])).trim(),
  classId: String(pickValue(row, ['classId', 'class_id', 'class id'])).trim(),
  guardianName: String(pickValue(row, ['guardianName', 'guardian', 'parentName', 'parent_name', 'guardian name', 'parent name'])).trim(),
  guardianPhone: String(pickValue(row, ['guardianPhone', 'guardianTel', 'parentPhone', 'parent_phone', 'guardian phone', 'guardian telephone', 'parent phone'])).trim(),
  guardianEmail: String(pickValue(row, ['guardianEmail', 'parentEmail', 'parent_email', 'guardian email', 'parent email'])).trim(),
  status: 'Ready'
});

const validateHeaders = (headers = []) => {
  const normalizedHeaders = headers.map((header) => normalizeHeader(header));
  const missingColumns = [];

  if (!normalizedHeaders.some((header) => ['firstname', 'first_name', 'studentfirstname'].includes(header))) missingColumns.push('firstName');
  if (!normalizedHeaders.some((header) => ['lastname', 'last_name', 'surname', 'studentlastname'].includes(header))) missingColumns.push('lastName');
  if (!normalizedHeaders.some((header) => ['dob', 'dateofbirth', 'date_of_birth'].includes(header))) missingColumns.push('dob');
  if (!normalizedHeaders.some((header) => ['gender', 'sex'].includes(header))) missingColumns.push('gender/sex');
  if (!normalizedHeaders.some((header) => ['classname', 'class', 'classid', 'class_id'].includes(header))) missingColumns.push('className');

  if (missingColumns.length > 0) {
    throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
  }
};

const StudentImportDialog = ({ open, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [parsedStudents, setParsedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // const [error, setError] = useState(null);
  // const [success, setSuccess] = useState(false);
  // const [importStats, setImportStats] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async e => {
    setError('');
    // setSuccess(false);
    // setImportStats(null);

    const f = e.target.files[0];
    if (!f) {
      setFile(null);
      setPreview([]);
      setParsedStudents([]);
      return;
    }

    const validTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    const validExtensions = ['.csv', '.xlsx', '.xls'];

    const fileExtension = '.' + f.name.split('.').pop().toLowerCase();

    if (!validTypes.includes(f.type) && !validExtensions.includes(fileExtension)) {
      setError('Please upload a valid CSV or Excel file (.csv, .xlsx, .xls).');
      setPreview([]);
      setParsedStudents([]);
      setFile(null);
      return;
    }

    setFile(f);
    setLoading(true);

    try {
      const { previewRows, students } = await parseFileForPreviewAndStudents(f);
      setPreview(previewRows);
      setParsedStudents(students);
    } catch (err) {
      setError(`Failed to parse file: ${err.message}`);
      setPreview([]);
      setParsedStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const parseFileForPreviewAndStudents = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let previewRows = [];
          let students = [];

          const workbook = file.name.endsWith('.csv')
            ? XLSX.read(data, { type: 'string' })
            : XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const sheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          const headers = Array.isArray(sheetRows[0]) ? sheetRows[0] : [];

          validateHeaders(headers);

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          students = jsonData.map((row, index) => toStudentRow(row, index)).filter((student) => student.firstName || student.lastName || student.className || student.classId);
          previewRows = students.slice(0, 10);

          resolve({ previewRows, students });
        } catch (error) {
          reject(error);
        }
      };
      if (file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');

    try {
      if (typeof onImport === 'function') {
        await onImport(parsedStudents);
      }

      resetForm();
      onClose();
    } catch (err) {
      setError(err?.userMessage || err?.data?.details || err?.data?.error || err?.message || 'Failed to import students.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview([]);
    setParsedStudents([]);
    setError('');
    // setError(null);
    // setSuccess(false);
    // setImportStats(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
      const headers = {};
      if (token) headers['Authorization'] = token.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}`;
      if (schoolId) headers['X-Scope-School-Id'] = schoolId;
      if (phaseCode) headers['X-Active-Phase'] = phaseCode;
      if (phaseId) headers['X-Active-Phase-Id'] = phaseId;
      if (ayId) headers['X-Academic-Year-Id'] = ayId;
      const response = await fetch(resolveApiUrl('/api/admissions/template'), {
        headers,
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Template download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'student-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(downloadError?.message || 'Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 600
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Bulk Student Import
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Import Guide */}
          <Box sx={{
            p: 3,
            bgcolor: 'background.default',
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
              📋 Import Guidelines
            </Typography>

            <Card sx={{ bgcolor: 'background.paper', mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Required Fields (Student Info First):
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1, mb: 2 }}>
                    {[
                      'firstName', 'lastName',
                      'dob (DD-MM-YYYY)', 'gender/sex', 'className'
                    ].map(field => (
                      <Chip
                        key={field}
                        label={field}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                    📝 Optional Fields
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['placeOfBirth', 'address', 'guardianName', 'guardianPhone', 'guardianEmail'].map(field => (
                      <Chip
                        key={field}
                        label={field}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="info.main" sx={{ mb: 2, display: 'block' }}>
                    Note: Guardian email is recommended for account creation. If provided, credentials will be sent to the guardian.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                  >
                    Download Template
                  </Button>
                </Box>
              </CardContent>
            </Card>


          </Box>

          {/* Comprehensive Import Guidelines */}
          <Box sx={{
            p: 3,
            bgcolor: 'text.primary',
            color: 'background.default',
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
              📋 Detailed Import Guidelines
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'info.contrastText' }}>
                Required Fields (Exact Order):
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  1. <strong>firstName</strong> - Student's first name
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  2. <strong>lastName</strong> - Student's last name
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  3. <strong>dob</strong> - Date of birth in <strong>DD-MM-YYYY</strong> format (e.g., 31-12-2010)
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  4. <strong>placeOfBirth</strong> - Place of birth in
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  5. <strong>gender</strong> or <strong>sex</strong> - Must be exactly <strong>Male</strong> or <strong>Female</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  6. <strong>address</strong> - Student's residential address
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  7. <strong>className</strong> - Must match an existing class in your school database
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'info.contrastText' }}>
                Guardian Information (Optional):
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  8. <strong>guardianName</strong> - Full name of guardian
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  9. <strong>guardianEmail</strong> - Guardian's email address
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  10. <strong>guardianPhone</strong> - Guardian's phone number
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'info.contrastText' }}>
                📌 Important Notes:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20, color: 'info.contrastText' }}>
                <li><Typography variant="body2">All required fields must be filled</Typography></li>
                <li><Typography variant="body2">Class names must exist in your school database</Typography></li>
                <li><Typography variant="body2">Use exact format: DD-MM-YYYY for dates</Typography></li>
                <li><Typography variant="body2">Use exact values: Male/Female for gender</Typography></li>
                <li><Typography variant="body2">Guardian name is only needed when guardian phone or email is provided</Typography></li>
                <li><Typography variant="body2">Email addresses must be valid format</Typography></li>
                <li><Typography variant="body2">Phone numbers can include country code</Typography></li>
              </ul>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'info.contrastText' }}>
                📊 Supported File Types:
              </Typography>
              <Typography variant="body2">.csv, .xlsx, .xls files</Typography>
            </Box>

            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                📥 Sample Data Format:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
                <strong>Sample Data Format:</strong><br />
                <code>John,Doe,31-12-2010,Male,123 Main St,Yaounde,Class 1A,Jane Doe,+0987654321,jane.doe@parent.com,Mother</code>
                <br /><small><em>Fields marked with * are required. All others are optional.</em></small>
              </Typography>
            </Box>
          </Box>

          {/* File Upload Area */}
          <Box sx={{ p: 3 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: file ? 'success.main' : 'grey.400',
                bgcolor: file ? 'success.50' : 'grey.50',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
            >
              {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Drop your file here or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports CSV, Excel (.xlsx, .xls) files up to 10MB
                </Typography>

                {file && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      icon={<DescriptionIcon />}
                      label={file.name}
                      color="success"
                      variant="outlined"
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      File ready for import
                    </Typography>
                  </Box>
                )}
              </Box>

              {preview.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Preview ({preview.length} rows)
                  </Typography>
                  <div style={{ height: 300, width: '100%' }}>
                    <DataGrid
                      rows={preview}
                      columns={[
                        { field: 'row', headerName: '#', width: 60 },
                        { field: 'firstName', headerName: 'First Name', width: 120 },
                        { field: 'lastName', headerName: 'Last Name', width: 120 },
                        { field: 'className', headerName: 'Class', width: 100 },
                        { field: 'status', headerName: 'Status', width: 80 }
                      ]}
                      pageSize={5}
                      rowsPerPageOptions={[5, 10]}
                      checkboxSelection
                      disableSelectionOnClick
                    />
                  </div>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          color="primary"
          variant="contained"
          disabled={!file || loading || preview.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
        >
          {loading ? 'Importing...' : 'Import Students'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentImportDialog;
